import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useParams,
  useNavigate
} from "react-router-dom";
import {
  ArrowLeft,
  Plus,
  Bell,
  Clock,
  FileText,
  Receipt as ReceiptIcon,
  Eye,
  Send
} from "lucide-react";
import {
  getStudentFeeLedger,
  getStudentFeeLedgerPortal,
  getReceipts,
  getReceiptsPortal,
  getReceiptById,
  getReceiptByIdPortal,
  assignCustomStudentFee,
  assignCustomStudentFeePortal,
  getAllFeeHeads,
  getAllFeeHeadsPortal,
  applyLateFee as applyLateFeeApi,
  applyLateFeePortal as applyLateFeePortalApi
} from "../../api/fees/feeApi";
import {
  getStudentById
} from "../../api/student/studentApi";
import {
  getStudentByIdForPortal
} from "../../api/student/studentPortalApi";
import {
  getAllAcademicYears,
  getAllAcademicYearsPortal
} from "../../api/academicyear/academicYearApi";
import FeeReceiptModal from "../../components/fees/FeeReceiptModal";
import FeeSendNotificationModal from "../../components/whatsapp/FeeSendNotificationModal";
import ChannelSendIcons from "../../components/notifications/ChannelSendIcons";
import {
  expandLedgerList
} from "../../utils/feeLedgerDisplay";
import {
  Button,
  Input,
  Select,
  Checkbox,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Badge,
  Modal,
  EmptyState,
  Skeleton
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const ledgerStatusVariant = {
  PAID: "success",
  PARTIAL: "warning",
  UNPAID: "danger",
  OVERDUE: "danger",
};

const StudentFeeLedger = ({ isPortal = false, canManage = false }) => {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [summary, setSummary] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [receiptData, setReceiptData] = useState(null);

  const [customOpen, setCustomOpen] = useState(false);
  const [feeHeads, setFeeHeads] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [customForm, setCustomForm] = useState({
    academicYearId: "",
    feeHeadId: "",
    title: "",
    amount: "",
    dueDate: "",
    isPublished: true,
  });
  const [customLoading, setCustomLoading] = useState(false);
  const [applyingLateFee, setApplyingLateFee] = useState(false);

  const toast = useToast();

  // Notification modal — notifyReceiptId=null means general reminder
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyReceiptId, setNotifyReceiptId] = useState(null);

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const [stuRes, ledRes, recRes] = await Promise.all([
        isPortal ? getStudentByIdForPortal(studentId) : getStudentById(studentId),
        isPortal ? getStudentFeeLedgerPortal(studentId) : getStudentFeeLedger(studentId),
        isPortal
          ? getReceiptsPortal({ studentId })
          : getReceipts({ studentId }),
      ]);
      setStudent(stuRes?.data || stuRes);
      const payload = ledRes.data || ledRes || {};
      setLedger(payload.ledger || []);
      setSummary(payload.summary || null);
      setReceipts(recRes?.data || recRes || []);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load fee ledger.");
    } finally {
      setLoading(false);
    }
  }, [studentId, isPortal]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canManage) return;
    (isPortal ? getAllFeeHeadsPortal({ isActive: true }) : getAllFeeHeads({ isActive: true }))
      .then((r) => setFeeHeads(r.data || r || []))
      .catch(() => {});
    (isPortal ? getAllAcademicYearsPortal() : getAllAcademicYears())
      .then((r) => {
        const years = r?.data || r || [];
        setAcademicYears(Array.isArray(years) ? years : []);
      })
      .catch(() => {});
  }, [canManage, isPortal]);

  const viewReceipt = async (id) => {
    try {
      const res = isPortal ? await getReceiptByIdPortal(id) : await getReceiptById(id);
      setReceiptData(res.data || res);
      setReceiptOpen(true);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to load receipt details.");
    }
  };

  const submitCustom = async (e) => {
    e.preventDefault();
    setCustomLoading(true);
    try {
      const payload = {
        studentId,
        academicYearId: customForm.academicYearId,
        feeHeadId: customForm.feeHeadId,
        title: customForm.title.trim(),
        amount: Number(customForm.amount),
        dueDate: customForm.dueDate,
        isPublished: customForm.isPublished,
      };
      if (isPortal) await assignCustomStudentFeePortal(payload);
      else await assignCustomStudentFee(payload);
      toast.success("Custom fee assigned successfully.");
      setCustomOpen(false);
      setCustomForm({
        academicYearId: "",
        feeHeadId: "",
        title: "",
        amount: "",
        dueDate: "",
        isPublished: true,
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to assign custom fee.");
    } finally {
      setCustomLoading(false);
    }
  };

  const handleApplyLateFee = async () => {
    setApplyingLateFee(true);
    try {
      if (isPortal) await applyLateFeePortalApi(studentId);
      else await applyLateFeeApi(studentId);
      toast.success("Late fee applied successfully.");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to apply late fee.");
    } finally {
      setApplyingLateFee(false);
    }
  };

  const openReminderModal = () => {
    setNotifyReceiptId(null);
    setNotifyOpen(true);
  };

  const openReceiptNotifyModal = (receiptId) => {
    setNotifyReceiptId(receiptId);
    setNotifyOpen(true);
  };

  const studentName = useMemo(() => {
    if (!student) return "";
    return [student.firstName, student.middleName, student.lastName]
      .filter(Boolean)
      .join(" ");
  }, [student]);

  // Form option derivations
  const academicYearOptions = useMemo(
    () => [
      { label: "Select Academic Year", value: "" },
      ...academicYears.map((y) => ({ label: y.yearName, value: y._id })),
    ],
    [academicYears]
  );

  const feeHeadOptions = useMemo(
    () => [
      { label: "Select Fee Head", value: "" },
      ...feeHeads.map((h) => ({ label: h.name, value: h._id })),
    ],
    [feeHeads]
  );

  // Table row derivations
  const ledgerTableRows = useMemo(
    () =>
      expandLedgerList(ledger).map((r) => ({
        ...r,
        id: r.key,
        netFormatted: r.netAmount.toFixed(2),
        fineFormatted: r.fineAmount.toFixed(2),
        paidFormatted: r.paidAmount.toFixed(2),
        dueFormatted: r.dueAmount.toFixed(2),
        dueDateFormatted:
          r.isFirst && r.dueDate
            ? new Date(r.dueDate).toLocaleDateString("en-IN")
            : r.isFirst
            ? "—"
            : "",
      })),
    [ledger]
  );

  const receiptTableRows = useMemo(
    () =>
      receipts.map((r) => ({
        ...r,
        id: r._id,
        paymentDateFormatted: r.paymentDate
          ? new Date(r.paymentDate).toLocaleDateString("en-IN")
          : "—",
        paidFormatted: Number(r.totalPaid || 0).toFixed(2),
      })),
    [receipts]
  );

  const ledgerColumns = [
    {
      key: "billTitle",
      header: "Title",
      render: (r) => (
        <div>
          {r.isFirst ? (
            <span className="font-medium text-neutral-900 dark:text-neutral-100">{r.billTitle}</span>
          ) : null}
          {r.isFirst && r.headCount > 1 && (
            <span className="ml-1 text-xs text-neutral-400 dark:text-neutral-500">({r.headCount} heads)</span>
          )}
        </div>
      ),
    },
    {
      key: "headTitle",
      header: "Head",
      render: (r) => <span className="font-medium text-neutral-800 dark:text-neutral-100">{r.headTitle}</span>,
    },
    {
      key: "netFormatted",
      header: "Net",
      align: "right",
      render: (r) => (
        <div>
          <span>₹{r.netFormatted}</span>
          {r.fineAmount > 0 && (
            <div className="text-[10px] text-warning-700">
              incl. fine ₹{r.fineFormatted}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "paidFormatted",
      header: "Paid",
      align: "right",
      render: (r) => <span className="text-success-700">₹{r.paidFormatted}</span>,
    },
    {
      key: "dueFormatted",
      header: "Due",
      align: "right",
      render: (r) => (
        <span className={r.dueAmount > 0 ? "font-medium text-danger-500" : ""}>
          ₹{r.dueFormatted}
        </span>
      ),
    },
    {
      key: "dueDateFormatted",
      header: "Due Date",
      render: (r) => r.dueDateFormatted,
    },
    {
      key: "status",
      header: "Status",
      render: (r) => (
        <Badge variant={ledgerStatusVariant[r.status] || "default"}>
          {r.status}
        </Badge>
      ),
    },
  ];

  const receiptColumns = [
    {
      key: "receiptNo",
      header: "Receipt No",
      render: (r) => <span className="font-medium text-neutral-900 dark:text-neutral-100">{r.receiptNo}</span>,
    },
    {
      key: "paymentDateFormatted",
      header: "Date",
      render: (r) => r.paymentDateFormatted,
    },
    {
      key: "paidFormatted",
      header: "Amount",
      align: "right",
      render: (r) => `₹${r.paidFormatted}`,
    },
    {
      key: "paymentMode",
      header: "Mode",
      render: (r) => r.paymentMode,
    },
    {
      key: "isVoided",
      header: "Status",
      render: (r) =>
        r.isVoided ? (
          <Badge variant="danger">VOIDED</Badge>
        ) : (
          <Badge variant="success">OK</Badge>
        ),
    },
    {
      key: "actions",
      header: "Action",
      align: "right",
      nowrap: true,
      render: (r) => (
        <div className="flex flex-nowrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => viewReceipt(r._id)}
            leftIcon={<Eye className="h-3.5 w-3.5" />}
          >
            View
          </Button>
          {canManage && !r.isVoided && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => openReceiptNotifyModal(r._id)}
              leftIcon={<Send className="h-3.5 w-3.5" />}
              className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            >
              Notify
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      {/* Top Navigation */}
      <div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(-1)}
          leftIcon={<ArrowLeft className="h-4 w-4" />}
        >
          Back
        </Button>
      </div>

      {/* Header and Quick Actions */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Student Fee Ledger</h1>
          {student && (
            <p className="mt-1 text-sm text-neutral-500 dark:text-neutral-400">
              <span className="font-medium text-neutral-800 dark:text-neutral-100">{studentName}</span> · Adm:{" "}
              {student.admissionNo} · Roll: {student.rollNumber || "—"}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            isLoading={applyingLateFee}
            onClick={handleApplyLateFee}
            leftIcon={<Clock className="h-4 w-4" />}
          >
            Apply Late Fee
          </Button>

          {canManage && (
            <>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={openReminderModal}
                leftIcon={<Bell className="h-4 w-4" />}
                className="bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              >
                Send Reminder
              </Button>
              <div className="shrink-0">
                <ChannelSendIcons
                  studentId={studentId}
                  student={student}
                  isPortal={isPortal}
                  defaultEventType="DUE_REMINDER"
                />
              </div>
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={() => setCustomOpen(true)}
                leftIcon={<Plus className="h-4 w-4" />}
              >
                Custom Fee
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Status Alerts */}
      
      

      {loading ? (
        <div className="space-y-3 rounded-card border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 shadow-card dark:shadow-black/20">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex gap-4">
              <Skeleton height={16} width="20%" />
              <Skeleton height={16} width="25%" />
              <Skeleton height={16} width="15%" />
              <Skeleton height={16} width="20%" />
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium uppercase text-neutral-500 dark:text-neutral-400">Total Net</p>
                  <p className="mt-1 text-2xl font-bold text-neutral-900 dark:text-neutral-100">
                    ₹{Number(summary.totalNet || 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium uppercase text-neutral-500 dark:text-neutral-400">Paid</p>
                  <p className="mt-1 text-2xl font-bold text-success-700">
                    ₹{Number(summary.totalPaid || 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <p className="text-xs font-medium uppercase text-neutral-500 dark:text-neutral-400">
                    Outstanding
                  </p>
                  <p className="mt-1 text-2xl font-bold text-danger-500">
                    ₹{Number(summary.totalOutstanding || 0).toFixed(2)}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Fee Ledger Items Table */}
          <Card>
            <CardHeader className="border-b bg-neutral-50/50">
              <CardTitle>Fee Ledger</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table
                columns={ledgerColumns}
                data={ledgerTableRows}
                emptyState={
                  <EmptyState
                    icon={FileText}
                    title="No published items"
                    description="There are no ledger entries available for this student."
                  />
                }
              />
            </CardContent>
          </Card>

          {/* Receipts Table */}
          <Card>
            <CardHeader className="border-b bg-neutral-50/50">
              <CardTitle>Receipts</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <Table
                columns={receiptColumns}
                data={receiptTableRows}
                emptyState={
                  <EmptyState
                    icon={ReceiptIcon}
                    title="No receipts"
                    description="No payment receipts have been issued for this student yet."
                  />
                }
              />
            </CardContent>
          </Card>
        </>
      )}

      {/* Custom Fee Modal */}
      <Modal
        isOpen={customOpen}
        onClose={() => setCustomOpen(false)}
        title="Assign Custom Fee"
        footer={
          <>
            <Button variant="secondary" onClick={() => setCustomOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={submitCustom}
              isLoading={customLoading}
            >
              Assign
            </Button>
          </>
        }
      >
        <form onSubmit={submitCustom} className="space-y-4">
          <Select
            label="Academic Year"
            options={academicYearOptions}
            value={customForm.academicYearId}
            onChange={(val) => setCustomForm((p) => ({ ...p, academicYearId: val }))}
          />
          <Select
            label="Fee Head"
            options={feeHeadOptions}
            value={customForm.feeHeadId}
            onChange={(val) => setCustomForm((p) => ({ ...p, feeHeadId: val }))}
          />
          <Input
            label="Title"
            placeholder="Enter title"
            required
            value={customForm.title}
            onChange={(e) => setCustomForm((p) => ({ ...p, title: e.target.value }))}
          />
          <Input
            label="Amount"
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            required
            value={customForm.amount}
            onChange={(e) => setCustomForm((p) => ({ ...p, amount: e.target.value }))}
          />
          <Input
            label="Due Date"
            type="date"
            required
            value={customForm.dueDate}
            onChange={(e) => setCustomForm((p) => ({ ...p, dueDate: e.target.value }))}
          />
          <Checkbox
            label="Publish immediately"
            checked={customForm.isPublished}
            onChange={(e) =>
              setCustomForm((p) => ({ ...p, isPublished: e.target.checked }))
            }
          />
        </form>
      </Modal>

      {/* Specialized Action Modals */}
      <FeeReceiptModal
        isOpen={receiptOpen}
        onClose={() => setReceiptOpen(false)}
        receipt={receiptData}
      />

      <FeeSendNotificationModal
        isOpen={notifyOpen}
        onClose={() => setNotifyOpen(false)}
        studentId={studentId}
        receiptId={notifyReceiptId}
        isPortal={isPortal}
        defaultEventType="DUE_REMINDER"
      />
    </div>
  );
};

export default StudentFeeLedger;